import React from "react";
import { Drawer } from "@design-system/Drawer/Drawer";
import { Button } from "@design-system/Button/Button";
import { ColumnSettings } from "@design-system/ViewToolbar/ColumnSettings";
import { Separator } from "@design-system/Separator/Separator";
import { AllocationSummaryRows } from "./AllocationSummaryRows";
import { AllocationTotals } from "./allocationHelpers";
import { AllocationViewState } from "./useAllocationView";
import styles from "../EditAllocationDrawer.module.scss";

interface ShellProps {
    readonly open: boolean;
    readonly onClose: () => void;
    readonly headerAddon?: React.ReactNode;
    readonly children: React.ReactNode;
}

interface BodyProps {
    readonly hasAccrualLedger: boolean;
    readonly totals: AllocationTotals;
    readonly summaryAddon?: React.ReactNode;
    readonly canResolveDifference: boolean;
    readonly onResolveDifference: () => void;
    readonly secondaryAction: {
        label: string;
        onClick: () => void;
        isDisabled?: boolean;
    };
    readonly isSaveEnabled: boolean;
    readonly onSave: () => void;
    readonly children: React.ReactNode;
}

type LayoutProps = Omit<ShellProps, "headerAddon" | "children"> &
    BodyProps & { readonly viewState: AllocationViewState };

export const AllocationColumnSettings: React.FC<{
    readonly viewState: AllocationViewState;
}> = ({ viewState }) => (
    <ColumnSettings
        view={viewState.allocationView}
        viewConfig={viewState.allocationColumnConfig}
        defaultView={viewState.defaultAllocationView}
        onViewChange={viewState.setAllocationView}
    />
);

export const AllocationDrawerShell: React.FC<ShellProps> = ({
    open,
    onClose,
    headerAddon,
    children,
}) => (
    <Drawer
        open={open}
        onClose={onClose}
        size="fullScreen"
        noPadding
        contentClassName={styles.drawerRoot}
        headerAddonLeft="Edit allocation"
        headerAddonBeforeClose={
            headerAddon && (
                <div className={styles.headerRight}>{headerAddon}</div>
            )
        }
    >
        {children}
    </Drawer>
);

export const AllocationDrawerBody: React.FC<BodyProps> = ({
    hasAccrualLedger,
    totals,
    summaryAddon,
    canResolveDifference,
    onResolveDifference,
    secondaryAction,
    isSaveEnabled,
    onSave,
    children,
}) => (
    <div className={styles.drawerContent}>
        {hasAccrualLedger && (
            <div className={styles.subtitleBar}>
                <span>Step 2 of 2</span>
                <span>Edit allocation</span>
            </div>
        )}
        <div className={styles.tableArea}>{children}</div>

        <div className={styles.summaryIsland}>
            <AllocationSummaryRows allocation={totals} />
            {summaryAddon}
            <Separator />
            <div className={styles.actionFooter}>
                {hasAccrualLedger &&
                    canResolveDifference &&
                    totals.remainingAmount > 0 && (
                        <Button
                            variant="primary"
                            size="md"
                            onClick={onResolveDifference}
                        >
                            Resolve difference
                        </Button>
                    )}
                <Button
                    variant="primary"
                    size="md"
                    disabled={secondaryAction.isDisabled}
                    onClick={secondaryAction.onClick}
                >
                    {secondaryAction.label}
                </Button>
                <Button
                    variant="primary"
                    size="md"
                    disabled={!isSaveEnabled}
                    onClick={onSave}
                >
                    Save
                </Button>
            </div>
        </div>
    </div>
);

export const AllocationDrawerLayout: React.FC<LayoutProps> = ({
    open,
    onClose,
    viewState,
    ...bodyProps
}) => (
    <AllocationDrawerShell
        open={open}
        onClose={onClose}
        headerAddon={<AllocationColumnSettings viewState={viewState} />}
    >
        <AllocationDrawerBody {...bodyProps} />
    </AllocationDrawerShell>
);
